// packages/ui/src/sidebar/ExplorerPanel.tsx
//
// The file Explorer — the workspace search, the file tree per workspace root,
// and the empty state. Extracted from the former Sidebar so the sidebar can host
// multiple registered panels. Behavior is unchanged.
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type ReactElement,
} from 'react';
import type { FileHandleRef, PlatformBridge, ToolHost } from '@slate/core';
import {
  ChevronDown,
  ChevronRight,
  Clipboard,
  ClipboardPaste,
  FilePlus,
  FileUp,
  Folder,
  FolderOpen,
  FolderPlus,
  FolderTree,
  Plus,
  X,
} from 'lucide-react';
import { useDocStore } from '../store/docStore';
import { shortcutLabel } from '../keymap/platform';
import { FileTree, type FileTreeHandle } from '../components/FileTree';
import { useFsClipboardStore } from '../store/fsClipboardStore';
import { useContextMenu, type ContextMenuEntry } from '../context-menu';
import { useToolContributionsFor } from '../tools/useToolContributionsFor';
import { resolveContributionIcon } from '../tools/contributionIcon';
import { Modal } from '../components/Modal';
import { SidebarSearch } from '../components/SidebarSearch';
import { makeSidebarFileActions } from './sidebarActions';
import type { SidebarPanelProps } from './SidebarPanelRegistry';

export interface ExplorerPanelProps extends SidebarPanelProps {
  platform: PlatformBridge;
  toolHost?: ToolHost;
}

export function ExplorerPanel({ platform, toolHost }: ExplorerPanelProps): ReactElement {
  const workspaceRoots = useDocStore((s) => s.workspaceRoots);
  const removeWorkspaceRoot = useDocStore((s) => s.removeWorkspaceRoot);

  const canFolders = platform.capabilities.folders;
  const canSearch =
    platform.capabilities.search === true && typeof platform.searchFiles === 'function';

  const fileActions = useMemo(() => makeSidebarFileActions(platform), [platform]);

  const [closingRoot, setClosingRoot] = useState<FileHandleRef | null>(null);
  const confirmCloseRoot = useCallback(() => {
    if (closingRoot) removeWorkspaceRoot(closingRoot.id);
    setClosingRoot(null);
  }, [closingRoot, removeWorkspaceRoot]);

  const [searchActive, setSearchActive] = useState(false);
  const [collapsedRoots, setCollapsedRoots] = useState<Set<string>>(new Set());
  const treeRefs = useRef(new Map<string, FileTreeHandle | null>());
  const { openContextMenu } = useContextMenu();
  const rootContributions = useToolContributionsFor('workspaceRootContextMenu');

  const openRootMenu = useCallback(
    (x: number, y: number, root: FileHandleRef): void => {
      const canMutate = platform.capabilities.fsMutations === true;
      const handle = treeRefs.current.get(root.id);
      const clip = useFsClipboardStore.getState();
      const canPaste = clip.mode != null && clip.paths.length > 0;
      const items: ContextMenuEntry[] = [];
      if (canMutate) {
        items.push(
          {
            id: 'new-file',
            label: 'New File…',
            icon: FilePlus,
            onSelect: () => handle?.startCreateInRoot('file'),
          },
          {
            id: 'new-folder',
            label: 'New Folder…',
            icon: FolderPlus,
            onSelect: () => handle?.startCreateInRoot('folder'),
          },
          { type: 'separator', id: 'sep-create' },
          {
            id: 'paste',
            label: 'Paste',
            icon: ClipboardPaste,
            shortcut: shortcutLabel('V'),
            disabled: !canPaste,
            onSelect: () => void handle?.pasteIntoRoot(),
          },
          { type: 'separator', id: 'sep-clip' },
        );
      }
      items.push({
        id: 'copy-path',
        label: 'Copy path',
        icon: Clipboard,
        onSelect: () => {
          if (root.path) void platform.writeClipboardText(root.path).catch(() => {});
        },
      });
      if (toolHost && rootContributions.length > 0) {
        const args = { rootId: root.id, path: root.path ?? '' };
        const visible = rootContributions.filter(
          (c) => c.visible === undefined || c.visible(args, toolHost),
        );
        if (visible.length > 0) {
          items.push({ type: 'separator', id: 'sep-tools' });
          for (const c of visible) {
            const label = typeof c.label === 'function' ? c.label(args) : c.label;
            items.push({
              id: c.id,
              label,
              icon: resolveContributionIcon(c.icon),
              onSelect: () => {
                void c.run(args, toolHost);
              },
            });
          }
        }
      }
      openContextMenu(x, y, items);
    },
    [platform, openContextMenu, rootContributions, toolHost],
  );

  const toggleRoot = useCallback((id: string): void => {
    setCollapsedRoots((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const hasRoots = workspaceRoots.length > 0;

  // When the sticky search bar is rendered, group headers must pin *below* it.
  // The modifier raises --mp-group-sticky-top from 0 to the bar's height (40px).
  const rootClass =
    'mp-explorer mp-sidebar__section mp-sidebar__files' +
    (canSearch ? ' mp-explorer--with-search' : '');

  return (
    <div className={rootClass}>
      {hasRoots && canFolders ? (
        <>
          {canSearch && (
            <SidebarSearch
              platform={platform}
              roots={workspaceRoots}
              onActiveChange={setSearchActive}
            />
          )}
          {!searchActive &&
            workspaceRoots.map((root) => {
              const open = !collapsedRoots.has(root.id);
              const rootPath = root.path ?? '';
              const onGroupDragOver = (e: ReactDragEvent): void => {
                if (!rootPath) return;
                e.preventDefault();
                e.stopPropagation();
                e.dataTransfer.dropEffect = e.altKey ? 'copy' : 'move';
                treeRefs.current.get(root.id)?.reportHover(e.clientX, e.clientY, e.altKey);
              };
              const onGroupDragLeave = (e: ReactDragEvent): void => {
                if (e.currentTarget !== e.target) return;
                treeRefs.current.get(root.id)?.reportHoverEnd();
              };
              const onGroupDrop = (e: ReactDragEvent): void => {
                if (!rootPath) return;
                e.preventDefault();
                e.stopPropagation();
                const internal = e.dataTransfer.getData('application/x-slate-fs');
                let sources: string[] = [];
                if (internal) {
                  try {
                    sources = JSON.parse(internal);
                  } catch {
                    sources = [];
                  }
                }
                const handle = treeRefs.current.get(root.id);
                void handle?.dropAtRoot(sources, e.altKey);
              };
              return (
                <div
                  className="mp-sidebar__group"
                  key={root.id}
                  data-tree-root-path={rootPath || undefined}
                  onDragOver={onGroupDragOver}
                  onDragLeave={onGroupDragLeave}
                  onDrop={onGroupDrop}
                >
                  <div
                    className="mp-sidebar__group-header"
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openRootMenu(e.clientX, e.clientY, root);
                    }}
                  >
                    <button
                      type="button"
                      className="mp-sidebar__group-toggle"
                      aria-expanded={open}
                      title={root.path ?? root.name}
                      onClick={() => toggleRoot(root.id)}
                    >
                      {open ? (
                        <ChevronDown size={13} className="mp-sidebar__group-chevron" aria-hidden />
                      ) : (
                        <ChevronRight size={13} className="mp-sidebar__group-chevron" aria-hidden />
                      )}
                      <Folder size={14} className="mp-sidebar__group-icon" aria-hidden />
                      <span className="mp-sidebar__group-name">{root.name}</span>
                    </button>
                    {platform.capabilities.fsMutations && (
                      <button
                        type="button"
                        className="mp-iconbtn mp-sidebar__group-add"
                        aria-label={`Add to ${root.name}`}
                        title="New file or folder here"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openRootMenu(e.clientX, e.clientY, root);
                        }}
                      >
                        <Plus size={13} aria-hidden />
                      </button>
                    )}
                    <button
                      type="button"
                      className="mp-iconbtn mp-sidebar__group-remove"
                      aria-label="Remove folder"
                      title="Remove folder"
                      onClick={() => setClosingRoot(root)}
                    >
                      <X size={13} aria-hidden />
                    </button>
                  </div>
                  {open && root.path && (
                    <div className="mp-sidebar__group-body">
                      <FileTree
                        ref={(el) => {
                          if (el) treeRefs.current.set(root.id, el);
                          else treeRefs.current.delete(root.id);
                        }}
                        platform={platform}
                        rootPath={root.path}
                        label={`Files in ${root.name}`}
                        toolHost={toolHost}
                      />
                    </div>
                  )}
                </div>
              );
            })}
        </>
      ) : (
        <div className="mp-sidebar__empty">
          <FolderTree size={24} aria-hidden className="mp-sidebar__empty-icon" />
          {canFolders ? (
            <>
              <p className="mp-sidebar__empty-title">No folder open</p>
              <div className="mp-sidebar__empty-actions">
                <button type="button" className="mp-btn" onClick={() => void fileActions.onOpenFolder()}>
                  <FolderOpen size={14} aria-hidden />
                  Open Folder
                  <span className="mp-kbd">{shortcutLabel('O', { shift: true })}</span>
                </button>
                <button type="button" className="mp-btn" onClick={() => void fileActions.onOpenFile()}>
                  <FileUp size={14} aria-hidden />
                  Open File
                  <span className="mp-kbd">{shortcutLabel('O')}</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="mp-sidebar__empty-note">Folder browsing isn’t available in this browser.</p>
              <button type="button" className="mp-btn" onClick={() => void fileActions.onOpenFile()}>
                <FileUp size={14} aria-hidden />
                Open File
                <span className="mp-kbd">{shortcutLabel('O')}</span>
              </button>
            </>
          )}
        </div>
      )}

      <Modal
        open={closingRoot !== null}
        onClose={() => setClosingRoot(null)}
        title="Close folder"
        footer={
          <>
            <button type="button" className="mp-btn" onClick={() => setClosingRoot(null)}>
              Cancel
            </button>
            <button type="button" className="mp-btn mp-btn--primary" onClick={confirmCloseRoot}>
              Close folder
            </button>
          </>
        }
      >
        <p className="mp-modal__text">
          Remove “{closingRoot?.name}” from the workspace? The folder and its files stay on disk.
        </p>
      </Modal>
    </div>
  );
}
